import React, { useState, useEffect } from 'react';

interface ZPDPreviewProps {
  variant?: 'card' | 'featured';
}

const ZPDPreview: React.FC<ZPDPreviewProps> = ({ variant = 'card' }) => {
  const [studentLevel, setStudentLevel] = useState(45);
  const [scaffoldLevel, setScaffoldLevel] = useState(0);
  const [isScaffolding, setIsScaffolding] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsScaffolding(true);
      // Animate scaffold support
      let scaffoldProgress = 0;
      const scaffoldInterval = setInterval(() => {
        scaffoldProgress += 5;
        setScaffoldLevel(scaffoldProgress);
        if (scaffoldProgress >= 25) {
          clearInterval(scaffoldInterval);
          // Student advances with scaffold support
          setTimeout(() => {
            setStudentLevel(prev => {
              const newLevel = prev + 8;
              if (newLevel > 65) {
                // Reset for demo
                setScaffoldLevel(0);
                setIsScaffolding(false);
                return 45;
              }
              return newLevel;
            });
            setScaffoldLevel(0);
            setIsScaffolding(false);
          }, 500);
        }
      }, 100);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const height = variant === 'featured' ? 'h-48' : 'h-32';

  return (
    <div
      className={`relative ${height} w-full rounded-xl overflow-hidden`}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      {/* Header */}
      <div
        className="px-3 py-1.5 flex items-center justify-between"
        style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
      >
        <span className="text-[10px] font-semibold" style={{ color: '#94a3b8' }}>
          Zone of Proximal Development
        </span>
        {isScaffolding && (
          <div className="flex items-center gap-1">
            <div
              className="w-2 h-2 border border-t-transparent rounded-full animate-spin"
              style={{ borderColor: '#3b82f6', borderTopColor: 'transparent' }}
            />
            <span className="text-[9px]" style={{ color: '#3b82f6' }}>Scaffolding</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Zone visualization */}
        <div className="relative h-8 rounded-lg overflow-hidden mb-2" style={{ background: 'rgba(255,255,255,0.05)' }}>
          {/* Zone backgrounds */}
          <div
            className="absolute top-0 bottom-0 left-0"
            style={{
              width: '40%',
              background: 'rgba(16, 185, 129, 0.2)',
              borderRight: '1px dashed rgba(255,255,255,0.2)',
            }}
          />
          <div
            className="absolute top-0 bottom-0"
            style={{
              left: '40%',
              width: '30%',
              background: 'rgba(59, 130, 246, 0.2)',
              borderRight: '1px dashed rgba(255,255,255,0.2)',
            }}
          />
          <div
            className="absolute top-0 bottom-0 right-0"
            style={{
              width: '30%',
              background: 'rgba(239, 68, 68, 0.15)',
            }}
          />

          {/* Student marker */}
          <div
            className="absolute top-1 bottom-1 w-3 rounded transition-all duration-500 flex items-center justify-center"
            style={{
              left: `${studentLevel}%`,
              transform: 'translateX(-50%)',
              background: studentLevel < 40 ? '#10b981' : studentLevel < 70 ? '#3b82f6' : '#ef4444',
              boxShadow: '0 0 10px rgba(59, 130, 246, 0.5)',
            }}
          >
            <span className="text-[8px]">👤</span>
          </div>

          {/* Scaffold support indicator */}
          {scaffoldLevel > 0 && (
            <div
              className="absolute top-0 bottom-0 rounded-r transition-all duration-200"
              style={{
                left: `${studentLevel}%`,
                width: `${scaffoldLevel}%`,
                background: 'linear-gradient(90deg, rgba(139, 92, 246, 0.4), rgba(139, 92, 246, 0.1))',
                border: '1px solid rgba(139, 92, 246, 0.3)',
              }}
            />
          )}
        </div>

        {/* Zone labels */}
        <div className="flex justify-between text-[8px] mb-2">
          <span style={{ color: '#10b981' }}>Can Do</span>
          <span style={{ color: '#3b82f6' }}>With Help (ZPD)</span>
          <span style={{ color: '#ef4444' }}>Too Hard</span>
        </div>

        {/* Scaffold explanation */}
        <div
          className="p-2 rounded-lg flex items-center gap-2"
          style={{
            background: isScaffolding ? 'rgba(139, 92, 246, 0.15)' : 'rgba(255, 255, 255, 0.03)',
            border: isScaffolding ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid rgba(255, 255, 255, 0.05)',
            transition: 'all 0.3s ease',
          }}
        >
          <span className="text-sm">{isScaffolding ? '🪜' : '💡'}</span>
          <span className="text-[9px]" style={{ color: isScaffolding ? '#a78bfa' : '#94a3b8' }}>
            {isScaffolding
              ? 'AI provides targeted hints to bridge the gap'
              : 'Learning happens best in the ZPD zone'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ZPDPreview;
